import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Typography,
  Grid,
  Stack,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Paper,
  InputBase,
} from "@mui/material";
import { EducationCard } from "./EducationCard";
import { HelpCard } from "../Shared/Cards/HelpCard";
import { ReferralCard } from "../Shared/Cards/ReferralCard";
import ReactHtmlParser from "html-react-parser";
import { useTranslation } from "react-i18next";
import {
  fetchFaqData,
  searchFaq,
  setFilterType,
  setSearchText,
  clearFaqData,
} from "../../store/Actions/faqActions";
import DefaultLayout from "../Layout/DefaultLayout";
import HintDisplay from "../Commons/HintDisplay";

const EducationListIndex = ({ globalEvent, setGlobalEvent, title }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const {
    data: faqData = [],
    groupsData,
    loading,
    error,
    filterType,
    searchText,
    searchListHL,
  } = useSelector((state) => state.faq);
  const [showSearch, setShowSearch] = useState(false);
  const [recCount, setRecCount] = useState(3);
  const [flagBlurOk, setFlagBlurOk] = useState(true);
  const [idFilter, setIdFilter] = useState(null);
  const [searchListItems, setSearchListItems] = useState(3);

  useEffect(() => {
    if (faqData.length === 0 && !loading && !error) {
      dispatch(fetchFaqData());
    }
  }, [dispatch]);

  // Re-fetch FAQ data when language changes
  useEffect(() => {
    dispatch(clearFaqData());
    dispatch(fetchFaqData());
  }, [i18n.language, dispatch]);

  useEffect(() => {
    dispatch(searchFaq(searchText, faqData, filterType));
  }, [searchText, faqData, filterType, dispatch]);

  const toggleFilter = (filter) => {
    dispatch(
      setFilterType(
        filterType.includes(filter)
          ? filterType.filter((f) => f !== filter)
          : [...filterType, filter]
      )
    );
  };

  const increaseRecCount = () => {
    //console.log("increaseRecCount", recCount + 3);
    setRecCount(recCount + 3);
  }

  const handleSearchInput = (text) => {
    setShowSearch(text !== "");
    setIdFilter(null);
    dispatch(setSearchText(text));
  };

  const filteredFaqData = faqData
    .filter((group) => {
      const matchesFilterType =
        filterType.length === 0 || filterType.includes(group.id);
      const matchesIdFilter =
        idFilter === null || group.items.some((item) => item.id === idFilter);
      return matchesFilterType && matchesIdFilter;
    })
    .map((group) => {
      if (idFilter === null) return group;
      const filteredItems = group.items.filter((item) => item.id === idFilter);
      return { ...group, items: filteredItems };
    });

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <>
      <DefaultLayout
        globalEvent={globalEvent}
        setGlobalEvent={setGlobalEvent}
        title={title}
      >
        <Box
          sx={{
            display: "flex",
            background:
              "linear-gradient(180deg, #C3F0FD 380px, transparent 0%)",
            justifyContent: { xl: "center" },
          }}
        >
          <Grid
            container
            sx={{
              mt: { xs: 0, sm: 8 },
              pl: { xs: 2, md: 27 },
              pr: { xs: 2, md: 17 },
              mb: 20,
              width: { xl: "1530px" },
              minHeight: "100vh",
              textAlign: "left",
            }}
          >
            <Grid item xs={12}>
              <Stack direction={"row"} justifyContent={"space-between"}>
                <Typography
                  variant={"h5"}
                  sx={{
                    pl: { xs: 2, sm: 0 },
                    margin: { xs: "1em", sm: 0 },
                    fontWeight: 700,
                  }}
                >
                  {typeof t("faq.title") === "string"
                    ? ReactHtmlParser(t("faq.title"))
                    : t("faq.title")}
                </Typography>
              </Stack>
              <Grid
                item
                xs={12}
                md={7}
                onMouseDown={() => setFlagBlurOk(false)}
              >
                <Paper
                  sx={{
                    p: "2px 4px",
                    display: "flex",
                    alignItems: "center",
                    borderRadius: "50px",
                    position: "relative",
                    zIndex: "2",
                    pr: 1,
                    boxShadow: showSearch
                      ? "0px 0px 16px 0px #00000014"
                      : "none",
                  }}
                >
                  <IconButton
                    sx={{
                      p: "12px",
                      display: showSearch ? "flex" : "none",
                      alignItems: "center",
                    }}
                    aria-label="menu"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <mask
                        id="mask0_886_29163"
                        Style="mask-type:alpha"
                        maskUnits="userSpaceOnUse"
                        x="0"
                        y="0"
                        width="24"
                        height="24"
                      >
                        <rect width="24" height="24" fill="#D9D9D9" />
                      </mask>
                      <g mask="url(#mask0_886_29163)">
                        <path
                          d="M21.4444 23L14.4444 16C13.8889 16.4444 13.25 16.7963 12.5278 17.0556C11.8056 17.3148 11.037 17.4444 10.2222 17.4444C8.2037 17.4444 6.49537 16.7454 5.09722 15.3472C3.69907 13.9491 3 12.2407 3 10.2222C3 8.2037 3.69907 6.49537 5.09722 5.09722C6.49537 3.69907 8.2037 3 10.2222 3C12.2407 3 13.9491 3.69907 15.3472 5.09722C16.7454 6.49537 17.4444 8.2037 17.4444 10.2222C17.4444 11.037 17.3148 11.8056 17.0556 12.5278C16.7963 13.25 16.4444 13.8889 16 14.4444L23 21.4444L21.4444 23ZM10.2222 15.2222C11.6111 15.2222 12.7917 14.7361 13.7639 13.7639C14.7361 12.7917 15.2222 11.6111 15.2222 10.2222C15.2222 8.83333 14.7361 7.65278 13.7639 6.68056C12.7917 5.70833 11.6111 5.22222 10.2222 5.22222C8.83333 5.22222 7.65278 5.70833 6.68056 6.68056C5.70833 7.65278 5.22222 8.83333 5.22222 10.2222C5.22222 11.6111 5.70833 12.7917 6.68056 13.7639C7.65278 14.7361 8.83333 15.2222 10.2222 15.2222Z"
                          fill="#2B3674"
                        />
                      </g>
                    </svg>
                  </IconButton>

                  <InputBase
                    sx={{ ml: 1, flex: 1, pt: 0 }}
                    placeholder={t("faq.searchbyterm")}
                    inputProps={{ "aria-label": "search" }}
                    onFocus={(ev) => setShowSearch(searchText !== "")}
                    onChange={(ev) => handleSearchInput(ev.target.value)}
                    onBlur={(ev) => {
                      if (flagBlurOk) {
                        setShowSearch(false);
                      } else {
                        setFlagBlurOk(true);
                        ev.preventDefault();
                        ev.target.focus();
                      }
                    }}
                  />
                  <IconButton
                    color="primary"
                    sx={{
                      p: "12px",
                      display: !showSearch ? "flex" : "none",
                      alignItems: "center",
                    }}
                    aria-label="directions"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <mask
                        id="mask0_886_29163"
                        Style="mask-type:alpha"
                        maskUnits="userSpaceOnUse"
                        x="0"
                        y="0"
                        width="24"
                        height="24"
                      >
                        <rect width="24" height="24" fill="#D9D9D9" />
                      </mask>
                      <g mask="url(#mask0_886_29163)">
                        <path
                          d="M21.4444 23L14.4444 16C13.8889 16.4444 13.25 16.7963 12.5278 17.0556C11.8056 17.3148 11.037 17.4444 10.2222 17.4444C8.2037 17.4444 6.49537 16.7454 5.09722 15.3472C3.69907 13.9491 3 12.2407 3 10.2222C3 8.2037 3.69907 6.49537 5.09722 5.09722C6.49537 3.69907 8.2037 3 10.2222 3C12.2407 3 13.9491 3.69907 15.3472 5.09722C16.7454 6.49537 17.4444 8.2037 17.4444 10.2222C17.4444 11.037 17.3148 11.8056 17.0556 12.5278C16.7963 13.25 16.4444 13.8889 16 14.4444L23 21.4444L21.4444 23ZM10.2222 15.2222C11.6111 15.2222 12.7917 14.7361 13.7639 13.7639C14.7361 12.7917 15.2222 11.6111 15.2222 10.2222C15.2222 8.83333 14.7361 7.65278 13.7639 6.68056C12.7917 5.70833 11.6111 5.22222 10.2222 5.22222C8.83333 5.22222 7.65278 5.70833 6.68056 6.68056C5.70833 7.65278 5.22222 8.83333 5.22222 10.2222C5.22222 11.6111 5.70833 12.7917 6.68056 13.7639C7.65278 14.7361 8.83333 15.2222 10.2222 15.2222Z"
                          fill="#2B3674"
                        />
                      </g>
                    </svg>
                  </IconButton>
                </Paper>

                <div
                  style={{ position: "relative", width: "100%", zIndex: "2" }}
                >
                  <div
                    show={showSearch}
                    style={{
                      backgroundColor: "#FFFFFF",
                      position: "absolute",
                      width: "100%",
                      marginTop: "-1.2em",
                      display: showSearch ? "block" : "none",
                      borderBottomLeftRadius: "30px",
                      borderBottomRightRadius: "30px",
                      boxShadow:
                        "0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)",
                    }}
                  >
                    {" "}
                    {searchListHL.length ? (
                      <ul style={{ padding: "1.5em", paddingTop: "0" }}>
                        {searchListHL.slice(0, searchListItems).map((i) => {
                          return (
                            <li
                              style={{
                                listStyleType: "none",
                                cursor: "pointer",
                              }}
                              onClick={(ev) => {
                                setIdFilter(i.id);
                                setShowSearch(false);
                              }}
                            >
                              <div>
                                <Typography
                                  variant="body4"
                                  sx={{
                                    marginTop: "2em",
                                    fontWeight: 700,
                                  }}
                                >
                                  {typeof i.title === "string"
                                    ? ReactHtmlParser(i.title)
                                    : i.title}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    mt: 6,
                                    fontWeight: 400,
                                    color: "primary.main",
                                  }}
                                >
                                  {typeof i.content === "string"
                                    ? ReactHtmlParser(i.content)
                                    : i.content}
                                </Typography>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <>
                        <Box sx={{ mt: "1em", textAlign: "center" }}>
                          <svg
                            width="74"
                            height="66"
                            viewBox="0 0 74 66"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M58.8333 56.3333L50.9167 64.25C50.1528 65.0139 49.1806 65.3958 48 65.3958C46.8195 65.3958 45.8472 65.0139 45.0833 64.25C44.3194 63.4861 43.9375 62.5139 43.9375 61.3333C43.9375 60.1528 44.3194 59.1806 45.0833 58.4167L53 50.5L45.0833 42.5833C44.3194 41.8195 43.9375 40.8472 43.9375 39.6667C43.9375 38.4861 44.3194 37.5139 45.0833 36.75C45.8472 35.9861 46.8195 35.6042 48 35.6042C49.1806 35.6042 50.1528 35.9861 50.9167 36.75L58.8333 44.6667L66.75 36.75C67.5139 35.9861 68.4861 35.6042 69.6667 35.6042C70.8472 35.6042 71.8195 35.9861 72.5833 36.75C73.3472 37.5139 73.7292 38.4861 73.7292 39.6667C73.7292 40.8472 73.3472 41.8195 72.5833 42.5833L64.6667 50.5L72.5833 58.4167C73.3472 59.1806 73.7292 60.1528 73.7292 61.3333C73.7292 62.5139 73.3472 63.4861 72.5833 64.25C71.8195 65.0139 70.8472 65.3958 69.6667 65.3958C68.4861 65.3958 67.5139 65.0139 66.75 64.25L58.8333 56.3333ZM4.66667 42.1667C3.48611 42.1667 2.49653 41.7674 1.69792 40.9688C0.899306 40.1701 0.5 39.1806 0.5 38C0.5 36.8194 0.899306 35.8299 1.69792 35.0313C2.49653 34.2326 3.48611 33.8333 4.66667 33.8333H25.5C26.6806 33.8333 27.6701 34.2326 28.4688 35.0313C29.2674 35.8299 29.6667 36.8194 29.6667 38C29.6667 39.1806 29.2674 40.1701 28.4688 40.9688C27.6701 41.7674 26.6806 42.1667 25.5 42.1667H4.66667ZM4.66667 25.5C3.48611 25.5 2.49653 25.1007 1.69792 24.3021C0.899306 23.5035 0.5 22.5139 0.5 21.3333C0.5 20.1528 0.899306 19.1632 1.69792 18.3646C2.49653 17.566 3.48611 17.1667 4.66667 17.1667H42.1667C43.3472 17.1667 44.3368 17.566 45.1354 18.3646C45.934 19.1632 46.3333 20.1528 46.3333 21.3333C46.3333 22.5139 45.934 23.5035 45.1354 24.3021C44.3368 25.1007 43.3472 25.5 42.1667 25.5H4.66667ZM4.66667 8.83333C3.48611 8.83333 2.49653 8.43403 1.69792 7.63542C0.899306 6.83681 0.5 5.84722 0.5 4.66667C0.5 3.48611 0.899306 2.49653 1.69792 1.69792C2.49653 0.899306 3.48611 0.5 4.66667 0.5H42.1667C43.3472 0.5 44.3368 0.899306 45.1354 1.69792C45.934 2.49653 46.3333 3.48611 46.3333 4.66667C46.3333 5.84722 45.934 6.83681 45.1354 7.63542C44.3368 8.43403 43.3472 8.83333 42.1667 8.83333H4.66667Z"
                              fill="#2B3674"
                            />
                          </svg>
                        </Box>
                        <Box sx={{ mb: "1em", textAlign: "center" }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 400,
                              color: "primary.main",
                            }}
                          >
                            {typeof t("faq.noresult") === "string"
                              ? ReactHtmlParser(t("faq.noresult"))
                              : t("faq.noresult")}
                          </Typography>
                        </Box>
                      </>
                    )}
                    {searchListHL.length ? (
                      <Box
                        sx={{
                          textAlign: "left",
                          paddingLeft: "1em",
                          borderTop: "solid 1px #EBEBEB",
                          zIndex: "1000",
                        }}
                      >
                        <Button
                          onClick={(event) => {
                            //setIdFilter(0);
                            //setShowSearch(false);
                            setSearchListItems(searchListItems + 2);
                          }}
                          variant={"text"}
                          sx={{ color: "primary.light" }}
                        >
                          {typeof t("faq.loadmore") === "string"
                            ? ReactHtmlParser(t("faq.loadmore"))
                            : t("faq.loadmore")}
                        </Button>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          textAlign: "left",
                          paddingLeft: "1em",
                          borderTop: "solid 1px #EBEBEB",
                        }}
                      >
                        <Button
                          onClick={(event) => {
                            setSearchText("");
                            setShowSearch(false);
                          }}
                          variant={"text"}
                          sx={{ color: "primary.light" }}
                        >
                          {typeof t("faq.close") === "string"
                            ? ReactHtmlParser(t("faq.close"))
                            : t("faq.close")}
                        </Button>
                      </Box>
                    )}
                  </div>
                </div>
              </Grid>

              <Stack direction={"row"} justifyContent={"space-between"}>
                <Typography
                  variant="body3"
                  sx={{ mt: 3, pl: { xs: 2, sm: 0 }, fontWeight: 700 }}
                >
                  {typeof t("faq.searchbytopic") === "string"
                    ? ReactHtmlParser(t("faq.searchbytopic"))
                    : t("faq.searchbytopic")}
                </Typography>
              </Stack>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "right",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Grid container>
                    {groupsData.map((group) => (
                      <Grid
                        item
                        xs={6}
                        sm={4}
                        lg={2}
                        key={"group" + group.title}
                      >
                        <Card
                          onClick={() => toggleFilter(group.id)}
                          sx={{
                            boxShadow: "0px 0px 16px 0px #0000001A",
                            borderRadius: "16px",
                            width: "160px",
                            height: "140px",
                            display: "flex",
                            cursor: "pointer",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "1em",
                          }}
                        >
                          <CardContent>
                            <Stack
                              direction={"column"}
                              spacing={1}
                              justifyContent={"center"}
                              alignItems={"center"}
                            >
                              <Stack
                                direction="column"
                                sx={{ height: 50, alignItems: "center" }}
                              >
                                {typeof group.icon === "string"
                                  ? ReactHtmlParser(
                                      group.icon.replace(
                                        "fill='inherit'",
                                        `fill='${
                                          filterType.includes(group.id)
                                            ? "#62C275"
                                            : "#2B3674"
                                        }'`
                                      )
                                    )
                                  : group.icon}
                              </Stack>
                              <Typography
                                variant="body3"
                                sx={{
                                  fontSize: "14px",
                                  lineHeight: "17.64px",
                                  fontWeight: 700,
                                  textAlign: "center",
                                  color: filterType.includes(group.id)
                                    ? "#62C275"
                                    : "auto",
                                }}
                              >
                                {typeof group.title === "string"
                                  ? ReactHtmlParser(group.title)
                                  : group.title}
                              </Typography>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={7} mt={3}>
              {filteredFaqData.map((group) => (
                <Box sx={{ mb: 4 }} key={group.id}>
                  <Stack direction={"row"} alignItems="center" sx={{ mb: 2 }}>
                    {typeof group.icon === "string"
                      ? ReactHtmlParser(
                          group.icon.replace(
                            "<svg",
                            '<svg style="fill: #2B3674; height: 24px; width: 24px; max-height: 24px;"'
                          )
                        )
                      : group.icon}
                    <Typography
                      variant="body3"
                      sx={{
                        ml: 2,
                        fontSize: 16,
                        fontWeight: 700,
                      }}
                    >
                      {typeof group.title === "string"
                        ? ReactHtmlParser(group.title)
                        : group.title}
                    </Typography>
                    <div
                      style={{
                        flexGrow: 1,
                        background: "transparent",
                        height: "1px",
                        borderTop: "1px solid #EBEBEB",
                        marginLeft: "20px",
                      }}
                    ></div>
                  </Stack>
                  <Stack spacing={2}>
                    {group.items.slice(0, recCount).map((item) => (
                      <EducationCard
                        key={item.id}
                        icon={
                          typeof group.icon === "string"
                            ? ReactHtmlParser(group.icon)
                            : group.icon
                        }
                        title={item.title}
                        content={item.content}
                      />
                    ))}
                  </Stack>
                  {recCount < group.items.length && (
                    <Box sx={{ textAlign: "center", mt: 3 }}>
                      <Button
                        onClick={() => increaseRecCount()}
                        variant={"text"}
                        sx={{ color: "primary.light" }}
                      >
                        {typeof t("faq.loadmore") === "string"
                          ? ReactHtmlParser(t("faq.loadmore"))
                          : t("faq.loadmore")}
                      </Button>
                    </Box>
                  )}
                </Box>
              ))}
            </Grid>

            <Grid item xs={12} md={5} sx={{ pl: { xs: 0, md: 6 }, mt: 11 }}>
              <Stack direction={"column"} spacing={2}>
                <HelpCard />
                <HintDisplay />
                <ReferralCard />
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </DefaultLayout>
    </>
  );
};

export default EducationListIndex;
